import { SCHEMA } from '@vedalib/editor/lib/brand'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldNumber,
  FieldNumberArray,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS, OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { TestMultipleChoiceFormBrandType } from './testMultipleChoiceTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.testMultipleChoice.style, {
  checkbox: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.testMultipleChoice.schema.flagColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  item: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  item_hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  border: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testMultipleChoice.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  text: {
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('input.label.innerAnswerIndents'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
  },
  offset: {
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('input.label.answerIndents'),
      props: { width: 64 },
    }),
  },
})

const TestMultipleChoiceFormStyle: TestMultipleChoiceFormBrandType = ({
  device,
  subConfigs,
  setForm,
  edited,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  const handleSetFeedback = useCallback(() => setForm?.(BrandFormEnum.FEEDBACK), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.testMultipleChoice.tags.checkbox')}</Typography>
        <FormField config={schema.checkbox.color} />
        <FormField config={schema.item.backgroundColor} />
        <FormField config={schema.item_hover.backgroundColor} />
        <FormField config={schema.border.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />
        <FormField config={schema.text.padding} />
        <FormField config={schema.offset.marginTop} />

        <TileButton
          description={t('uiKit.button.feedbackDescription')}
          icon='otherRight'
          name='showFeedbackForm'
          onClick={handleSetFeedback}
        >
          <Typography weight='medium'>{t('page.brand.tab.feedback')}</Typography>
          {isAnyEdited(edited.feedback) && <BrandTagEdited />}
        </TileButton>

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(TestMultipleChoiceFormStyle) as typeof TestMultipleChoiceFormStyle
